import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PropertyMap = ({ properties, selectedProperty, onPropertySelect, filters }) => {
  const [mapCenter] = useState({ lat: 5.3364, lng: -4.0267 }); // Abidjan coordinates
  const [showMapControls, setShowMapControls] = useState(true);

  const communeColors = {
    'cocody': '#C9A84C', // brand-gold
    'plateau': '#D97706', 
    'marcory': '#EF4444',
    'yopougon': '#10B981',
    'adjame': '#F59E0B',
    'koumassi': '#E11D48',
    'port-bouet': '#B45309',
    'treichville': '#3B82F6',
    'abobo': '#D97706',
    'attécoubé': '#DC2626'
  };

  const priceRanges = [
    { min: 0, max: 50000000, color: '#10B981', label: '< 50M XOF' },
    { min: 50000000, max: 100000000, color: '#F59E0B', label: '50M - 100M XOF' },
    { min: 100000000, max: 200000000, color: '#EF4444', label: '100M - 200M XOF' },
    { min: 200000000, max: Infinity, color: '#9F1239', label: '> 200M XOF' }
  ];

  const getPriceRangeColor = (price) => {
    const range = priceRanges?.find(r => price >= r?.min && price < r?.max);
    return range ? range?.color : '#C9A84C';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact'
    })?.format(price);
  };

  return (
    <div className="relative h-full bg-[#111111] rounded-3xl overflow-hidden shadow-2xl border-none outline-none">
      
      {/* Map Container */}
      <div className="relative w-full h-full">
        {/* On applique un filtre CSS pour assombrir Google Maps si possible, sinon on garde la carte standard */}
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title="Abidjan Properties Map"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=12&output=embed`}
          className="border-none outline-none filter contrast-125 saturate-50"
        />

        {/* Map Overlay with Property Markers */}
        <div className="absolute inset-0 pointer-events-none">
          {properties?.map((property, index) => {
            const markerColor = getPriceRangeColor(property?.price);
            const isSelected = selectedProperty?.id === property?.id;
            
            return (
              <div
                key={property?.id}
                className="absolute pointer-events-auto cursor-pointer transform -translate-x-1/2 -translate-y-1/2 z-10"
                style={{
                  left: `${20 + (index % 8) * 10}%`,
                  top: `${20 + Math.floor(index / 8) * 15}%`
                }}
                onClick={() => onPropertySelect(property)}
              >
                {/* Property Marker (Dark UI Style) */}
                <div
                  className={`relative w-8 h-8 rounded-full border-2 shadow-lg transition-all duration-300 hover:scale-125 ${
                    isSelected 
                      ? 'scale-125 border-brand-gold shadow-[0_0_15px_rgba(201,168,76,0.8)] z-50' 
                      : 'border-[#111111]'
                  }`}
                  style={{ backgroundColor: markerColor }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon name="Home" size={14} color="white" />
                  </div>
                </div>

                {/* Property Info Popup */}
                {isSelected && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-64 bg-black/80 backdrop-blur-md rounded-2xl shadow-2xl p-4 z-50 border border-white/10">
                    <div className="space-y-3">
                      <h4 className="font-heading font-bold text-sm text-white line-clamp-2 leading-snug">
                        {property?.title}
                      </h4>
                      <div className="flex items-center space-x-1.5 text-white/50">
                        <Icon name="MapPin" size={14} />
                        <span className="text-xs font-light">{property?.commune}</span>
                      </div>
                      
                      <div className="flex items-center justify-between pt-2 border-t border-white/10">
                        <span className="font-heading font-bold text-brand-gold">
                          {formatPrice(property?.price)}
                        </span>
                        <span className="text-xs text-white/40 font-light">
                          {property?.area}m²
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-3 text-white/70">
                        {property?.bedrooms && (
                          <div className="flex items-center space-x-1 bg-white/5 px-2 py-1 rounded-md">
                            <Icon name="Bed" size={12} className="text-brand-gold" />
                            <span className="text-xs font-bold">{property?.bedrooms}</span>
                          </div>
                        )}
                        {property?.bathrooms && (
                          <div className="flex items-center space-x-1 bg-white/5 px-2 py-1 rounded-md">
                            <Icon name="Bath" size={12} className="text-brand-gold" />
                            <span className="text-xs font-bold">{property?.bathrooms}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Popup Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-black/80 backdrop-blur-md"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Map Controls (Légende flottante) */}
      {showMapControls && (
        <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-md border border-white/10 rounded-2xl shadow-2xl p-5 space-y-4 w-56 z-40">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading font-bold text-sm text-white tracking-widest uppercase">Légende</h4>
            <button
              onClick={() => setShowMapControls(false)}
              className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/20 transition-colors border-none"
            >
              <Icon name="X" size={12} color="white" />
            </button>
          </div>

          {/* Price Range Legend */}
          <div className="space-y-3">
            <h5 className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Prix (XOF)</h5>
            {priceRanges?.map((range, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: range?.color }}
                />
                <span className="text-xs text-white/70 font-medium">{range?.label}</span>
              </div>
            ))}
          </div>

          {/* Commune Legend */}
          <div className="space-y-3 pt-4 border-t border-white/10">
            <h5 className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Secteurs clés</h5>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(communeColors)?.slice(0, 6)?.map(([commune, color]) => (
                <div key={commune} className="flex items-center space-x-2">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-[10px] text-white/60 capitalize truncate">
                    {commune}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Map Controls Toggle Button */}
      {!showMapControls && (
        <div className="absolute top-4 right-4 z-40">
          <button
            onClick={() => setShowMapControls(true)}
            className="flex items-center gap-2 bg-black/80 backdrop-blur-md text-white border border-white/10 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-colors shadow-xl"
          >
            <Icon name="Info" size={14} />
            Légende
          </button>
        </div>
      )}

      {/* Property Count Badge */}
      <div className="absolute bottom-6 left-6 z-40">
        <div className="flex items-center space-x-2 bg-black/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 shadow-2xl">
          <Icon name="MapPin" size={16} className="text-brand-gold animate-pulse" />
          <span className="text-xs font-bold text-white uppercase tracking-widest">
            {properties?.length} propriétés
          </span>
        </div>
      </div>

    </div>
  );
};

export default PropertyMap;